/**
 * Briques partagées : routeur par hash (liens profonds `#/planning?contrat=&mois=`),
 * libellés v1 (§3.3 types, §3.4 statuts), utilitaires, composants génériques.
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { type ContratVisible, ErreurApi } from './api';

// ── Libellés v1 ──────────────────────────────────────────────────────────────

/** §3.3 — libellés v1 exacts. */
export const TYPES: Record<number, string> = {
  1: 'Travail', 2: 'Congé', 3: 'Certificat médical', 4: 'Petit chômage', 5: 'Congé sans solde', 6: 'Repos compensatoire', 7: 'Chômage temporaire',
};
/** Types à alignement demi-journée (astérisque v1, RG-PLN-011). */
export const TYPES_DEMI_JOURNEE = new Set([2, 3, 4, 6]);
/** §3.4 — statuts mensuels, libellés exacts du bandeau v1 (FrameMonPlanning / FrameMaPrestation). */
export function libelleStatut(cible: 'planning' | 'prestations', statut: number, valideur?: string, valideurValeur?: number, exempte = false): string {
  if (exempte || valideurValeur === -2) return 'Sans objet';
  if (cible === 'prestations' && valideurValeur === 999999) return 'Inconnu';
  const prefixe = cible === 'planning' ? 'Encodage' : 'Encodage/confirmation';
  const e = cible === 'planning' ? '' : '(e)';
  switch (statut) {
    case 1: return `${prefixe} en cours`;
    case 2: return `${prefixe} terminé${e} - en attente de validation par ${valideur ?? '…'}`;
    case 3: return `${prefixe} terminé${e} et validé${e}`;
    default: return cible === 'planning' ? 'Manquant - à encoder' : 'Les prestations doivent être encodées ou confirmées';
  }
}
/** Libellés courts (tableau de bord v2 / badges). */
export const STATUTS: Record<number, string> = { 0: 'Manquant - à encoder', 1: 'Encodage en cours', 2: 'En attente de validation', 3: 'Validé' };

// ── Dates et heures (convention murale : chaînes AAAA-MM-JJTHH:mm:ss sans fuseau) ──

export function moisCourant(): number { const d = new Date(); return d.getFullYear() * 100 + d.getMonth() + 1; }
export function moisSuivant(m: number, delta: 1 | -1): number {
  const an = Math.trunc(m / 100); const mois = (m % 100) + delta;
  if (mois === 0) return (an - 1) * 100 + 12; if (mois === 13) return (an + 1) * 100 + 1; return an * 100 + mois;
}
const NOMS_MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const NOMS_JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
export function libelleMois(m: number): string { return `${NOMS_MOIS[(m % 100) - 1]} ${Math.trunc(m / 100)}`; }
export function joursDuMois(m: number): string[] {
  const an = Math.trunc(m / 100); const mois = m % 100; const n = new Date(Date.UTC(an, mois, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${an}-${String(mois).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`);
}
/** 0 = lundi … 6 = dimanche. */
export function jourSemaine(iso: string): number { return (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7; }
export function heure(mural: string): string { return mural.slice(11, 16); }
export function jourDe(mural: string): string { return mural.slice(0, 10); }
/** Mois AAAAMM d'un jour ISO (AAAA-MM-JJ) — `MainForm.MoisEnCours := YearOf(ADate)*100+MonthOf(ADate)` (FrameMonPlanning.pas L1615). */
export function moisDuJour(iso: string): number { return Number(iso.slice(0, 4)) * 100 + Number(iso.slice(5, 7)); }
export function jjmmaaaa(iso: string | null | undefined): string { if (!iso) return ''; const [a, m, j] = iso.slice(0, 10).split('-'); return `${j}/${m}/${a}`; }
export function hhmm(minutes: number): string {
  const s = minutes < 0 ? '-' : ''; const a = Math.abs(minutes);
  return `${s}${String(Math.trunc(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}
export function minutesDe(hhmmStr: string): number { const [h, m] = hhmmStr.split(':').map(Number); return (h ?? 0) * 60 + (m ?? 0); }
export function minutesEvenement(e: { debut: string; fin: string }): number {
  return Math.round((new Date(`${e.fin}Z`).getTime() - new Date(`${e.debut}Z`).getTime()) / 60000);
}
export function dateAaaammjj(d: number): string { const s = String(d); return d === 0 ? '·' : `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`; }
export function messageErreur(e: unknown): string { return e instanceof ErreurApi ? e.message : e instanceof Error ? e.message : 'Erreur inattendue'; }

// ── Routeur par hash ─────────────────────────────────────────────────────────

export interface Route { chemin: string; params: URLSearchParams }
function lireRoute(): Route {
  const brut = window.location.hash.replace(/^#\/?/, '') || 'tableau-de-bord';
  const [chemin, query = ''] = brut.split('?');
  return { chemin: chemin ?? 'tableau-de-bord', params: new URLSearchParams(query) };
}
export function useRoute(): Route {
  const [route, setRoute] = useState(lireRoute);
  useEffect(() => { const f = () => setRoute(lireRoute()); window.addEventListener('hashchange', f); return () => window.removeEventListener('hashchange', f); }, []);
  return route;
}
export function naviguer(chemin: string, params?: Record<string, string | number | undefined>): void {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  window.location.hash = `#/${chemin}${s ? `?${s}` : ''}`;
}

// ── Chargement générique ─────────────────────────────────────────────────────

export function useChargement<T>(charge: () => Promise<T>, deps: unknown[]): { donnees: T | null; erreur: string | null; recharger: () => void; chargement: boolean } {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let actif = true;
    setChargement(true);
    charge().then((d) => { if (actif) { setDonnees(d); setErreur(null); } }).catch((e) => { if (actif) setErreur(messageErreur(e)); }).finally(() => { if (actif) setChargement(false); });
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const recharger = useCallback(() => setTick((t) => t + 1), []);
  return { donnees, erreur, recharger, chargement };
}

// ── Composants ───────────────────────────────────────────────────────────────

export function Modal({ titre, surFermeture, enfants, large }: { titre: string; surFermeture: () => void; enfants: ReactNode; large?: boolean }): JSX.Element {
  useEffect(() => { const f = (e: KeyboardEvent) => { if (e.key === 'Escape') surFermeture(); }; window.addEventListener('keydown', f); return () => window.removeEventListener('keydown', f); }, [surFermeture]);
  return (
    <div className="voile" onMouseDown={(e) => { if (e.target === e.currentTarget) surFermeture(); }}>
      <div className={`modale${large ? ' large' : ''}`} role="dialog" aria-modal="true" aria-label={titre}>
        <div className="modale-entete"><h2>{titre}</h2><button className="discret" aria-label="Fermer" onClick={surFermeture}>×</button></div>
        <div className="modale-corps">{enfants}</div>
      </div>
    </div>
  );
}

export function Champ({ libelle, enfants, aide }: { libelle: string; enfants: ReactNode; aide?: string }): JSX.Element {
  return <div className="champ"><label>{libelle}</label>{enfants}{aide && <small className="aide">{aide}</small>}</div>;
}

export function Erreur({ message }: { message: string | null }): JSX.Element | null {
  return message ? <p className="erreur" role="alert">{message}</p> : null;
}

export function Badge({ statut }: { statut: number }): JSX.Element {
  return <span className="badge" data-statut={statut}>{STATUTS[statut] ?? `Statut ${statut}`}</span>;
}

export function NavigationMois({ mois, surChangement }: { mois: number; surChangement: (m: number) => void }): JSX.Element {
  return (
    <div className="navigation-mois">
      <button aria-label="Mois précédent" onClick={() => surChangement(moisSuivant(mois, -1))}>←</button>
      <span className="mois">{libelleMois(mois)}</span>
      <button aria-label="Mois suivant" onClick={() => surChangement(moisSuivant(mois, 1))}>→</button>
      <button className="discret" onClick={() => surChangement(moisCourant())}>Aujourd'hui</button>
    </div>
  );
}

/** Sélection du travailleur (7.1) — filtrée par droits/service/QFQ côté serveur. */
export function SelecteurContrat({ equipe, valeur, surChangement }: { equipe: ContratVisible[]; valeur: number; surChangement: (id: number) => void }): JSX.Element | null {
  if (equipe.length <= 1) return null;
  return (
    <select className="selecteur-contrat" value={valeur} onChange={(e) => surChangement(Number(e.target.value))} aria-label="Travailleur">
      {equipe.map((c) => (
        <option key={c.id} value={c.id}>
          {c.nom} {c.prenom}{c.estSoi ? ' (moi)' : ''}{c.estCdd ? ` — CDD ${jjmmaaaa(c.premierJourContrat)}${c.dernierJourContrat ? ` → ${jjmmaaaa(c.dernierJourContrat)}` : ''}` : ''}
        </option>
      ))}
    </select>
  );
}

export function Tuile({ libelle, valeur, accent }: { libelle: string; valeur: string; accent?: boolean }): JSX.Element {
  return <div className={`tuile${accent ? ' accent' : ''}`}><div className="libelle">{libelle}</div><div className="valeur">{valeur}</div></div>;
}

/** Lot 8 — applique le thème d'un opérateur (jetons CSS de styles.css). Un thème vide = style Théâtre National. */
export const VARIANTES_THEME = ['sombre', 'clair', 'contraste', 'doux', 'editorial'] as const;
export function appliquerTheme(style: { scene?: string; signal?: string; encre?: string; feutre?: string; barre?: string; policeTitres?: string; policeTexte?: string; googleFonts?: string | null; variante?: string | null } | null | undefined): void {
  const r = document.documentElement.style;
  // Variante d'aspect : classe theme-<variante> sur <html> (sombre = aucune classe, style Théâtre National).
  for (const v of VARIANTES_THEME) document.documentElement.classList.remove(`theme-${v}`);
  if (style?.variante && style.variante !== 'sombre' && (VARIANTES_THEME as readonly string[]).includes(style.variante)) document.documentElement.classList.add(`theme-${style.variante}`);
  const cles: Array<[string, string | undefined]> = [['--scene', style?.scene], ['--signal', style?.signal], ['--encre', style?.encre], ['--feutre', style?.feutre], ['--barre', style?.barre]];
  for (const [v, val] of cles) { if (val) r.setProperty(v, val); else r.removeProperty(v); }
  // Texte posé sur l'accent : blanc, ou noir si l'accent est trop clair pour du blanc (contraste WCAG < 3:1).
  if (style?.scene && contrasteAvecBlanc(style.scene) < 3) r.setProperty('--texte-accent', '#1b1b1b'); else r.removeProperty('--texte-accent');
  if (style?.policeTitres) r.setProperty('--display', `'${style.policeTitres}', 'Archivo Narrow', 'Arial Narrow', sans-serif`); else r.removeProperty('--display');
  if (style?.policeTexte) r.setProperty('--texte', `'${style.policeTexte}', system-ui, sans-serif`); else r.removeProperty('--texte');
  const id = 'police-operateur';
  document.getElementById(id)?.remove();
  if (style?.googleFonts) { const l = document.createElement('link'); l.id = id; l.rel = 'stylesheet'; l.href = style.googleFonts; document.head.appendChild(l); }
}

/** Rapport de contraste WCAG entre une couleur hexadécimale et le blanc. */
export function contrasteAvecBlanc(hex: string): number {
  const c = (v: number) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; };
  const n = Number.parseInt(hex.replace('#', ''), 16); if (Number.isNaN(n) || hex.length !== 7) return 21;
  const l = 0.2126 * c((n >> 16) & 255) + 0.7152 * c((n >> 8) & 255) + 0.0722 * c(n & 255);
  return 1.05 / (l + 0.05);
}
